import axiosInstance from "@/axios";
import seoMetadata from "@/_constants/seoMetadata";
import { getS3URL } from "@/_helpers/utilities";
import React, { useEffect, useState, useSyncExternalStore } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://yslearn.in";
const DEFAULT_IMAGE =
  "https://pub-561e50d39a1c4c3a9c5d9a7715790c10.r2.dev/images/logo.5aa40f66256266e8c528.png";

// Number of mounted CourseSeo instances. While a course page controls SEO,
// the site-wide DynamicSeoPage steps aside so the two don't overwrite each other.
let courseSeoCount = 0;
const courseSeoListeners = new Set();
const setCourseSeoCount = (delta) => {
  courseSeoCount += delta;
  courseSeoListeners.forEach((listener) => listener());
};
const subscribeCourseSeo = (listener) => {
  courseSeoListeners.add(listener);
  return () => courseSeoListeners.delete(listener);
};

const normalizePath = (pathname) => "/" + pathname.replace(/^\/+|\/+$/g, "");

const stripHtml = (text) =>
  !text || text === "null" || text === "undefined"
    ? ""
    : String(text).replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

const truncate = (text, max = 160) =>
  text.length > max ? text.slice(0, max - 3).replace(/\s+\S*$/, "") + "..." : text;

// Maps the SEO API response to the same shape as the static seoMetadata entries.
// Empty fields are dropped so they don't override fallbacks when merged.
const fromApi = (data) => {
  const seo = {
    title: data?.meta_title,
    description: data?.meta_description,
    keywords: data?.meta_keywords,
    ogTitle: data?.og_title,
    ogDescription: data?.og_description,
    ogImage: data?.og_image,
    twitterTitle: data?.twitter_title,
    twitterDescription: data?.twitter_description,
    twitterImage: data?.twitter_image,
  };
  Object.keys(seo).forEach((key) => !seo[key] && delete seo[key]);
  return seo;
};

// Fetches SEO configured in the admin for a path; returns null when there is none.
const useApiSeo = (path, skip) => {
  const [apiSeo, setApiSeo] = useState(null);

  useEffect(() => {
    setApiSeo(null);
    if (skip) return;

    const slug = path.replace(/^\//, "") || "home";
    let cancelled = false;

    axiosInstance
      .get(`/seo/slug/${slug}`)
      .then((res) => {
        if (!cancelled && res?.data?.data) setApiSeo(fromApi(res.data.data));
      })
      .catch((error) => console.error("Error fetching SEO data:", error));

    return () => {
      cancelled = true;
    };
  }, [path, skip]);

  return apiSeo;
};

// Builds SEO for a course/program page from the course record itself, so every
// course gets meaningful tags even without an entry in the SEO admin.
const fromCourse = (course) => {
  const name = course?.CourseName || course?.name;
  if (!name) return null;

  const description = truncate(
    stripHtml(course.courseOneLiner) ||
      stripHtml(course.Description || course.description) ||
      `Learn ${name} online with Yuvasakthi Academy through practical, industry-relevant training.`
  );
  const imageFile = Array.isArray(course.imageFiles)
    ? course.imageFiles[0]?.fileName
    : course.imageFiles?.fileName || course.fileName;
  const image = imageFile ? getS3URL(imageFile) : DEFAULT_IMAGE;
  const category = course.Category || course.category;
  const subCategory = course.SubCategory || course.subCategory;

  return {
    title: `${name} | Yuvasakthi Academy`,
    description,
    keywords: [name, `${name} course`, `${name} online course`, subCategory, category, "Yuvasakthi Academy", "online courses", "professional training"]
      .filter(Boolean)
      .join(", "),
    ogImage: image,
    ogImageAlt: `Yuvasakthi Academy - ${name}`,
    twitterImageAlt: `Yuvasakthi Academy - ${name}`,
    searchTitle: `${name} - Yuvasakthi Academy`,
    primaryTopic: name,
    primaryTaxonomy: ["Education", category, subCategory].filter(Boolean).join(" > "),
    pageType: "WebPage",
  };
};

const SeoTags = ({ seo, url }) => {
  const title = seo.title;
  const ogTitle = seo.ogTitle || title;
  const ogDescription = seo.ogDescription || seo.description;
  const ogImage = seo.ogImage || DEFAULT_IMAGE;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": seo.pageType || "WebPage",
    name: seo.searchTitle || title,
    description: seo.description,
    url,
    inLanguage: "en-IN",
    keywords: seo.focusArea || seo.keywords,
    genre: seo.primaryTaxonomy,
    about: seo.primaryTopic && { "@type": "Thing", name: seo.primaryTopic },
    primaryImageOfPage: { "@type": "ImageObject", url: ogImage },
    isPartOf: { "@type": "WebSite", name: "Yuvasakthi Academy", url: `${SITE_URL}/` },
  };

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {seo.description && <meta name="description" content={seo.description} />}
      {seo.keywords && <meta name="keywords" content={seo.keywords} />}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:locale" content={seo.ogLocale || "en_IN"} />
      <meta property="og:site_name" content={seo.ogSiteName || "Yuvasakthi Academy"} />
      {ogTitle && <meta property="og:title" content={ogTitle} />}
      {ogDescription && <meta property="og:description" content={ogDescription} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      {seo.ogImageWidth && <meta property="og:image:width" content={seo.ogImageWidth} />}
      {seo.ogImageHeight && <meta property="og:image:height" content={seo.ogImageHeight} />}
      {seo.ogImageAlt && <meta property="og:image:alt" content={seo.ogImageAlt} />}
      <meta property="og:type" content={seo.ogType || "website"} />

      {/* Twitter */}
      <meta name="twitter:card" content={seo.twitterCard || "summary_large_image"} />
      <meta name="twitter:site" content={seo.twitterSite || "@yslearn"} />
      <meta name="twitter:creator" content={seo.twitterCreator || "@yslearn"} />
      <meta name="twitter:url" content={seo.twitterUrl || url} />
      {(seo.twitterTitle || ogTitle) && (
        <meta name="twitter:title" content={seo.twitterTitle || ogTitle} />
      )}
      {(seo.twitterDescription || ogDescription) && (
        <meta name="twitter:description" content={seo.twitterDescription || ogDescription} />
      )}
      <meta name="twitter:image" content={seo.twitterImage || ogImage} />
      {seo.twitterImageWidth && <meta name="twitter:image:width" content={seo.twitterImageWidth} />}
      {seo.twitterImageHeight && <meta name="twitter:image:height" content={seo.twitterImageHeight} />}
      {seo.twitterImageAlt && <meta name="twitter:image:alt" content={seo.twitterImageAlt} />}

      {/* Additional SEO (non-standard; also included in the structured data below) */}
      {seo.searchTitle && <meta name="search-title" content={seo.searchTitle} />}
      {seo.focusArea && <meta name="focus-area" content={seo.focusArea} />}
      {seo.primaryTaxonomy && <meta name="primary-taxonomy" content={seo.primaryTaxonomy} />}
      {seo.primaryTopic && <meta name="primary-topic" content={seo.primaryTopic} />}
      {seo.pageType && <meta name="page-type" content={seo.pageType} />}

      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
};

// Site-wide SEO: static metadata for the pages in seoMetadata, otherwise
// whatever the SEO admin API has for the current path.
const DynamicSeoPage = () => {
  const location = useLocation();
  const path = normalizePath(location.pathname);
  const staticSeo = seoMetadata[path];
  const courseSeoActive = useSyncExternalStore(subscribeCourseSeo, () => courseSeoCount > 0);
  const apiSeo = useApiSeo(path, Boolean(staticSeo) || courseSeoActive);

  const seo = staticSeo || apiSeo;
  if (!seo || courseSeoActive) return null;

  return <SeoTags seo={seo} url={seo.ogUrl || `${SITE_URL}${path}`} />;
};

// SEO for course/program detail pages. Uses the SEO admin entry for the path
// when present, filling any gaps from the course data.
export const CourseSeo = ({ course }) => {
  const location = useLocation();
  const path = normalizePath(location.pathname);
  const apiSeo = useApiSeo(path, !course);
  const courseSeo = fromCourse(course);

  useEffect(() => {
    setCourseSeoCount(1);
    return () => setCourseSeoCount(-1);
  }, []);

  if (!courseSeo) return null;

  return <SeoTags seo={{ ...courseSeo, ...apiSeo }} url={`${SITE_URL}${path}`} />;
};

export default DynamicSeoPage;
